import React, { useState } from "react";
import useEvents from "hooks/useEvents";
import { formatDate, get12HoursTimeFromDateTime } from "utills/helper";
import BreadCrumb from "Common/BreadCrumb";
import { ToastContainer } from "react-toastify";
import { Plus, Video, ExternalLink } from "lucide-react";
import Modal from "Common/Components/Modal";
import EventForm from "Common/meetinForm";
import { useFocusedRow } from "hooks/useFocusedRecord";
import {
  FocusNotFound,
  focusRowClass,
  scrollRowIntoView,
} from "Common/FocusedRow";

const MeetingList = () => {
  const { data, isLoading, refetch } = useEvents({ page: 1, limit: 20 });
  const [showAdd, setShowAdd] = useState(false);

  // Followed here from an interview alert, whose referenceId is the event id.
  const { notFound: focusNotFound, dismissFocus, isFocused } = useFocusedRow(
    data,
    isLoading
  );

  const getJitsiLink = (roomName: string) => `https://meet.jit.si/${roomName}`;

  const handleDone = () => {
    setShowAdd(false);
    refetch();
  };

  return (
    <div>
      <BreadCrumb title="Meetings" pageTitle="Meetings" />
      <ToastContainer closeButton={false} limit={1} />

      {focusNotFound && <FocusNotFound onDismiss={dismissFocus} />}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="text-lg font-semibold text-gray-900">Upcoming Meetings</h2>
        <button
          onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors"
        >
          <Plus size={16} /> Create Meeting
        </button>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-lg border border-gray-200 py-12 text-center text-sm text-gray-400">
          Loading meetings…
        </div>
      ) : (data?.length ?? 0) === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 py-16 text-center">
          <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Video size={22} className="text-gray-400" />
          </div>
          <p className="text-sm text-gray-500">No upcoming meetings</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {data?.map((event) => {
            const roomName = `meet${event._id.substring(event._id.length - 10, event._id.length - 3)}`;
            const link = getJitsiLink(event?.roomName || roomName);
            return (
              <li
                key={event._id}
                ref={scrollRowIntoView(isFocused(event._id))}
                className={`bg-white rounded-lg border border-gray-200 p-4 flex flex-col lg:flex-row justify-between lg:items-center gap-4 hover:shadow-sm transition-shadow ${focusRowClass(
                  isFocused(event._id)
                )}`}
              >
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-gray-900">{event.title}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {formatDate(event.date)} · {get12HoursTimeFromDateTime(event.startDateTime)} – {get12HoursTimeFromDateTime(event.endDateTime)}
                  </p>
                </div>
                <a href={link} target="_blank" rel="noopener noreferrer">
                  <button className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors">
                    <ExternalLink size={14} /> Join
                  </button>
                </a>
              </li>
            );
          })}
        </ul>
      )}

      <Modal
        show={showAdd}
        onHide={() => setShowAdd(false)}
        id="meetingModal"
        modal-center="true"
        size="md"
      >
        <Modal.Header
          className="flex items-center justify-between px-5 py-4 border-b border-gray-200"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500"
        >
          <Modal.Title className="text-base font-semibold text-gray-900">Create Meeting</Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-5 py-4 overflow-y-auto">
          <EventForm onNext={handleDone} />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default MeetingList;
